import { ActionIcon, Button, Group, Select, TextInput } from '@mantine/core';
import { IconUser, IconUserPlus } from '@tabler/icons-react';
import classnames from 'classnames';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { OnboardInvitationSentModal } from 'src/Components/Modals/OnboardInvitationSentModal';
import { useAssetInviteUsersForm } from 'src/Hooks/Forms/useAssetInviteUsersForm';
import { useAssetInviteUser } from 'src/Hooks/Mutations/useAssetInviteUser';
import useUpdateLastAssetOnboardingInvitationDate from 'src/Hooks/Mutations/useUpdateLastAssetOnboardingInvitationDate';
import { CreateInvitationsDtoPresetEnum } from 'src/Services/API/generated';

import styles from './OnboardingInviteUsersForm.module.scss';
import { Props } from './OnboardingInviteUsersForm.types';

const OnboardingInviteUsersForm = ({ assetId }: Props) => {
  const { mutate: pingOnInvitation } = useUpdateLastAssetOnboardingInvitationDate();

  const { t } = useTranslation();
  const { mutate, isLoading } = useAssetInviteUser(assetId, {
    onSuccess: () => {
      setModalOpen(true);
      pingOnInvitation();
    },
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [invitationPreset, setInvitationPreset] = useState<CreateInvitationsDtoPresetEnum>(
    CreateInvitationsDtoPresetEnum.Contributor
  );

  const handleSubmit = useCallback(
    (values: { emails: { email: string }[] }) => {
      const emails = values.emails.map(item => item.email);
      mutate({ emails, preset: invitationPreset });
    },
    [mutate, invitationPreset]
  );

  const { form, addEmail, removeEmail } = useAssetInviteUsersForm();
  const navigate = useNavigate();

  const fields = form.values.emails.map((_, index) => (
    <Group key={index} mt="xs">
      <TextInput
        className={styles.input}
        leftSection={<IconUser color={'#0e5b61'} />}
        placeholder="email@example.com"
        {...form.getInputProps(`emails.${index}.email`)}
      />

      <ActionIcon onClick={() => removeEmail(index)}>
        <i className={classnames('bx', ' bx-trash')}></i>
      </ActionIcon>
    </Group>
  ));

  return (
    <>
      <OnboardInvitationSentModal open={modalOpen} assetId={assetId} />
      <form onSubmit={form.onSubmit(handleSubmit)} className={styles.container}>
        <p className={styles.title}>{t('onboarding.inviteUsers', 'Invitez vos co-gérants')}</p>
        <p className={styles.description}>
          {t(
            'onboarding.inviteUsersDescription',
            'Ajoutez les membres qui gèrent le bien avec vous'
          )}
        </p>

        {fields}

        <Select
          mt="md"
          label={t('onboarding.invitationRoleLabel', 'Rôle des personnes invitées')}
          description={t(
            'onboarding.invitationRoleDescription',
            "Choisi avant l'envoi — ajustable à tout moment depuis la Console d'administration"
          )}
          allowDeselect={false}
          value={invitationPreset}
          onChange={value => {
            if (value) setInvitationPreset(value as CreateInvitationsDtoPresetEnum);
          }}
          data={[
            {
              value: CreateInvitationsDtoPresetEnum.Viewer,
              label: t('permissions.presets.viewer', 'Visiteur'),
            },
            {
              value: CreateInvitationsDtoPresetEnum.Contributor,
              label: t('permissions.presets.contributor', 'Contributeur'),
            },
            {
              value: CreateInvitationsDtoPresetEnum.Manager,
              label: t('permissions.presets.manager', 'Gestionnaire'),
            },
          ]}
        />
        <div>
          <Button
            leftSection={<IconUserPlus size={22} />}
            variant="light"
            onClick={addEmail}
            type="button"
            className={styles.addMail}
          >
            {t('onboarding.addNewUser', 'Ajouter un nouveau co-gérant')}
          </Button>
        </div>
        <div className={styles.buttonLine}>
          <Button onClick={() => navigate(`/asset/${assetId}/admin`)} variant={'outline'}>
            {t('onboarding.skip', 'Passer')}
          </Button>
          <Button type="submit" loading={isLoading}>
            {t('onboarding.inviteUsers', 'Envoyer les invitations')}
          </Button>
        </div>
      </form>
    </>
  );
};

export default OnboardingInviteUsersForm;
